import React, { useState, useRef, useEffect } from "react";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  CalendarOutlined,
  TeamOutlined,
  FileTextOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownOutlined,
  CloseOutlined,
  EditOutlined,
  BankOutlined,
  PrinterOutlined,
  InfoCircleOutlined,
  InfoOutlined,
  DeleteRowOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Space,
  Tag,
  Empty,
  TimePicker,
  Tooltip,
  Switch,
} from "antd";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { navigate } from "../../../components/Customs/navigation";
import { SectionCard } from "../../../components/Rooms/history/History";
import { notify } from "../../../components/Customs/NotifyProvider";
import { ENDPOINTS } from "../../../Services/ENDPOINT";
import { GeneralService } from "../../../Services/GeneralService";
import { RoomServices } from "../../../Services/RoomServices";
import { css, T } from "../../../Page/Styles/css";
import generateBulkPDF from "../../Customs/generateBulkPDF";
import generateBulkA4PDF from "../../Customs/generateBulkA4PDF";
import { canViewModule, canCreateModule } from "../../../../utils/permissionUtils";

const MODULE_KEY = "bulk_booking";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const styles = {
  layout: { display: "grid", gridTemplateColumns: "340px 1fr", gap: 20, alignItems: "flex-start" },
  leftPanel: { display: "flex", width:"100%", flexDirection: "column", gap: 10 },
  rightPanel: { display: "flex", flexDirection: "column", gap: 16 },
  label: { fontSize: 11.5, fontWeight: 600, color: "#555", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  inputPrefix: { position: "absolute", left: 10, color: "#bbb", fontSize: 14, pointerEvents: "none" },
  input: { width: "100%", height: 38, border: "1.5px solid #e8e8e8", borderRadius: 8, fontSize: 13, color: "#222", outline: "none", background: "#fafafa", transition: "border .2s", boxSizing: "border-box" },
  textarea: { width: "100%", border: "1.5px solid #e8e8e8", borderRadius: 8, fontSize: 13, color: "#222", padding: "9px 12px", outline: "none", background: "#fafafa", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" },
  select: { width: "100%", height: 38, border: "1.5px solid #e8e8e8", borderRadius: 8, fontSize: 13, color: "#222", padding: "0 12px", outline: "none", background: "#fafafa", cursor: "pointer" },
  formGrid: { display: "grid", gap: 14, marginTop: 4 },
  numberInput: { display: "flex", alignItems: "center", gap: 10, height: 38, border: "1.5px solid #e8e8e8", borderRadius: 8, padding: "0 12px", background: "#fafafa", width: "fit-content" },
  numBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#555", lineHeight: 1, padding: "0 4px" },
  dropZone: { border: "2px dashed #d6e4ff", borderRadius: 10, padding: "28px 20px", textAlign: "center", cursor: "pointer", background: "#f0f5ff", transition: "border .2s" },
  docItem: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: 8, background: "#fafafa" },
  docBtn: { border: "none", background: "none", cursor: "pointer", fontSize: 15, color: "#1677ff", padding: 4 },
  priceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, color: "#444", padding: "5px 0", borderBottom: "1px dashed #f0f0f0", gap: 10 },
  cta: { display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 },
  roomCard: { border: "1.5px solid #e8e8e8", borderRadius: 10, padding: "12px 14px", background: "#fff", cursor: "pointer", transition: "all .2s", position: "relative" },
  roomCardSelected: { border: "1.5px solid #1677ff", background: "#f0f5ff" },
};

const STATUS_COLORS = {
  available: { bg: "#f6ffed", color: "#389e0d", border: "#b7eb8f" },
  occupied: { bg: "#fff1f0", color: "#cf1322", border: "#ffa39e" },
  reserved: { bg: "#fff7e6", color: "#d46b08", border: "#ffd591" },
  maintenance: { bg: "#f9f0ff", color: "#531dab", border: "#d3adf7" },
};

const toNum = (v) => {
  const n = Number(String(v ?? 0).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const nightsBetween = (ci, co) => {
  if (!ci || !co) return 0;
  const d = (new Date(co) - new Date(ci)) / 86400000;
  return d > 0 ? d : 1;
};

const FormField = ({ label, required, error, span = 1, children }) => (
  <div style={{ gridColumn: `span ${span}` }}>
    <label style={styles.label}>{label}{required && <span style={{ color: "#ff4d4f" }}> *</span>}</label>
    {children}
    {error && <div style={{ fontSize: 11, color: "#ff4d4f", marginTop: 3 }}>{error}</div>}
  </div>
);

const Input = ({ prefix, placeholder, value, onChange, type = "text" }) => (
  <div style={styles.inputWrap}>
    {prefix && <span style={styles.inputPrefix}>{prefix}</span>}
    <input
      type={type}
      placeholder={placeholder}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...styles.input, paddingLeft: prefix ? 34 : 12 }}
    />
  </div>
);

const Textarea = ({ placeholder, value, onChange, rows = 3 }) => (
  <textarea placeholder={placeholder} value={value || ""} rows={rows} onChange={(e) => onChange(e.target.value)} style={styles.textarea} />
);

const Select = ({ value, options = [], onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={styles.select}>
    {options.map((o) => {
      const label = typeof o === "object" ? o.label : o;
      const optionValue = typeof o === "object" ? o.value : o;
      return <option key={optionValue} value={optionValue}>{label}</option>;
    })}
  </select>
);

const NumberInput = ({ value = 0, min = 1, max, disabled = false, onChange }) => (
  <div style={{ ...styles.numberInput, opacity: disabled ? 0.55 : 1 }}>
    <button type="button" disabled={disabled} style={styles.numBtn} onClick={() => onChange(Math.max(min, Number(value || 0) - 1))}>−</button>
    <span style={{ minWidth: 28, textAlign: "center", fontWeight: 600, fontSize: 14 }}>{Number(value || 0)}</span>
    <button type="button" disabled={disabled} style={styles.numBtn} onClick={() => onChange(max != null ? Math.min(max, Number(value || 0) + 1) : Number(value || 0) + 1)}>+</button>
  </div>
);

const Divider = ({ style } = {}) => <div style={{ height: 1, background: "rgba(0,0,0,0.07)", margin: "14px 0", ...style }} />;

const StatusBadge = ({ status }) => {
  const cfg = STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS.available;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>{status}</span>;
};

const RoomSummaryCard = ({ rooms, selectedId, onSelect }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {rooms.map((room) => {
      const isSelected = selectedId === room.id;
      return (
        <div key={room.id} onClick={() => onSelect(room)} style={{ ...styles.roomCard, ...(isSelected ? styles.roomCardSelected : {}) }}>
          {isSelected && <div style={{ position: "absolute", top: 8, right: 8, width: 8, height: 8, borderRadius: "50%", background: "#1677ff" }} />}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>{room.room_name}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>#{room.room_number} · {room.room_type}</div>
            </div>
            <StatusBadge status={room.status} />
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 12, color: "#666", flexWrap: "wrap" }}>
            <span><TeamOutlined style={{ marginRight: 4 }} />{room.max_occupancy} adults</span>
            <span><BankOutlined style={{ marginRight: 4 }} />Floor {room.floor}</span>
            <span style={{ fontWeight: 600, color: "#1677ff" }}>₹{toNum(room.price_per_day).toLocaleString()}/night</span>
          </div>
        </div>
      );
    })}
  </div>
);

const getRoomCalc = ({ room, data = {}, nights, hourlyBook, totalHours, fullBooking }) => {
  const duration = hourlyBook ? totalHours : nights;
  const adults = Number(data.guests || 1);
  const child = Number(data.child || 0);
  const maxOccupancy = Number(room.max_occupancy || 0);
  const maxPersona = maxOccupancy + Number(room.additional_occupancy || 0);
  const extraGuests = Math.max(0, adults - maxOccupancy);
  const remainingNormalOccupancy = Math.max(0, maxOccupancy - adults);
  const extraChild = Math.max(0, child - remainingNormalOccupancy);
  const childCostPerPerson = toNum(room.child_cost_per_person || 0);
  const extraChildCostPerNight = extraChild * childCostPerPerson;

  const baseTotal = hourlyBook ? duration * toNum(room.price_per_hour) : duration * toNum(room.price_per_day);
  const extraCostPerNight = extraGuests * toNum(room.add_cost_per_occupancy);
  const extraTotal = extraCostPerNight * duration;
  const extraChildTotal = extraChildCostPerNight * duration;
  const acChargeTotal = data.ac_required !== false ? toNum(room.ac_charge) * nights : 0;
  const fullBookTotal = fullBooking ? (hourlyBook ? toNum(room.additional_24_hour) : toNum(room.additional_24_hour) * nights) : 0;
  const total = baseTotal + extraTotal + extraChildTotal + acChargeTotal + fullBookTotal;

  return {
    duration,
    adults,
    child,
    maxPersona,
    baseTotal,
    extraGuests,
    extraCostPerNight,
    extraTotal,
    extraChild,
    childCostPerPerson,
    extraChildCostPerNight,
    extraChildTotal,
    acChargeTotal,
    fullBookTotal,
    total,
  };
};

const PaymentRowsBox = ({ paymentRows, finalPayableTotal, finalRemaining, totalAdvance, totalDiscount, paymentStatus, addPaymentRow, updatePaymentRow, removePaymentRow }) => (
  <div style={{ marginTop: 10, padding: "14px", background: "#f6ffed", borderRadius: 8, border: "1px solid #b7eb8f" }}>
    <div style={{ fontWeight: 800, marginBottom: 10, color: "#237804" }}>Advance / Discount Details</div>

    {paymentRows.length === 0 && <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>No advance or discount added.</div>}

    {paymentRows.map((row) => (
      <div key={row.key} style={{
    display: "grid",
    gridTemplateColumns: "100px 1fr 40px",
    gap: 8,
    alignItems: "center",
}}>
        <Select
            style={{ width: 100 }}
          value={row.type}
          options={[{ label: "Advance", value: "advance" }, { label: "Discount", value: "discount" }]}
          onChange={(v) => updatePaymentRow(row.key, "type", v)}
        />
        <input
          type="number"
          min="0"
          value={row.amount}
          onChange={(e) => updatePaymentRow(row.key, "amount", Number(e.target.value || 0))}
          style={{ width: "100%", padding: "6px 12px", textAlign: "right", background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6, boxSizing: "border-box" }}
        />
        <Button danger type="text" icon={<DeleteRowOutlined />} onClick={() => removePaymentRow(row.key)} />
      </div>
    ))}

    <Space style={{ marginTop: 8, marginBottom: 10 }} wrap>
      <Button size="small" type="primary" disabled={finalRemaining <= 0 || finalPayableTotal <= 0} onClick={() => addPaymentRow("advance")}>+ Add Advance</Button>
      <Button size="small" disabled={finalRemaining <= 0 || finalPayableTotal <= 0} onClick={() => addPaymentRow("discount")}>+ Add Discount</Button>
    </Space>

    <Divider />
    <div style={styles.priceRow}><span>Total Discount</span><b style={{ color: "#cf1322" }}>₹{totalDiscount.toLocaleString()}</b></div>
    <div style={styles.priceRow}><span>Total Advance Paid</span><b>₹{totalAdvance.toLocaleString()}</b></div>
    <div style={styles.priceRow}><span>Remaining Payable</span><b>₹{finalRemaining.toLocaleString()}</b></div>
    <div style={styles.priceRow}><span>Payment Status</span><b>{paymentStatus}</b></div>
  </div>
);

const PricingTable = ({
  rooms,
  nights,
  roomData,
  loyaltyDiscountPercent = 0,
  gst = 0,
  gstType = "exclusive",
  onGstTypeChange,
  hourlyBook = false,
  totalHours = 1,
  fullBooking = false,
  earlyCheckinAmount = 0,
  paymentRows,
  finalPayableTotal,
  finalRemaining,
  totalAdvance,
  totalDiscount,
  paymentStatus,
  addPaymentRow,
  updatePaymentRow,
  removePaymentRow,
}) => {
  const rawRoomsTotal = rooms.reduce((sum, room) => sum + getRoomCalc({ room, data: roomData[room.id], nights, hourlyBook, totalHours, fullBooking }).total, 0);
  const rawGrandTotal = rawRoomsTotal + earlyCheckinAmount;
  const loyaltyPercent = Number(loyaltyDiscountPercent || 0);
  const loyaltyAmount = loyaltyPercent > 0 ? Number(((rawGrandTotal * loyaltyPercent) / 100).toFixed(2)) : 0;
  const payableTotal = Math.max(rawGrandTotal - loyaltyAmount, 0);
  const gstPercent = Number(gst || 0);
  const gstAmount = gstPercent > 0 ? Math.round((payableTotal * gstPercent) / 100) : 0;

  return (
    <div>
      {rooms.map((room) => {
        const calc = getRoomCalc({ room, data: roomData[room.id], nights, hourlyBook, totalHours, fullBooking });
        return (
          <div key={room.id} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 4 }}>
              {room.room_name} <span style={{ color: "#999", fontWeight: 400 }}>#{room.room_number}</span>
            </div>
            <div style={styles.priceRow}>
              <span>₹{toNum(hourlyBook ? room.price_per_hour : room.price_per_day).toLocaleString()} × {calc.duration || "—"} {hourlyBook ? (calc.duration === 1 ? "hour" : "hours") : (calc.duration === 1 ? "night" : "nights")}</span>
              <span style={{ fontWeight: 600 }}>₹{calc.baseTotal.toLocaleString()}</span>
            </div>
            {calc.extraGuests > 0 && <div style={styles.priceRow}><span>Extra guests ({calc.extraGuests})</span><span style={{ fontWeight: 600 }}>₹{calc.extraTotal.toLocaleString()}</span></div>}
            {calc.extraChild > 0 && <div style={styles.priceRow}><span>Extra Child ({calc.extraChild})</span><span style={{ fontWeight: 600 }}>₹{calc.extraChildTotal.toLocaleString()}</span></div>}
            {fullBooking && calc.fullBookTotal > 0 && <div style={styles.priceRow}><span style={{ color: "#237804" }}>24 Hours Booking</span><span style={{ fontWeight: 600, color: "#237804" }}>₹{calc.fullBookTotal.toLocaleString()}</span></div>}
            {toNum(room.ac_charge) > 0 && <div style={styles.priceRow}><span style={{ color: roomData[room.id]?.ac_required !== false ? "#237804" : "#999" }}>AC Charge {roomData[room.id]?.ac_required === false && "(optional)"}</span><span style={{ fontWeight: 600, color: roomData[room.id]?.ac_required !== false ? "#237804" : "#999" }}>{roomData[room.id]?.ac_required !== false ? `₹${calc.acChargeTotal.toLocaleString()}` : "—"}</span></div>}
            <div style={{ ...styles.priceRow, color: "#389e0d", fontWeight: 700 }}><span>Room Total</span><span>₹{calc.total.toLocaleString()}</span></div>
            <Divider style={{ margin: "8px 0" }} />
          </div>
        );
      })}

      {earlyCheckinAmount > 0 && <div style={styles.priceRow}><span>Early Check-in Charge</span><span style={{ fontWeight: 600, color: "#cf1322" }}>+₹{earlyCheckinAmount.toLocaleString()}</span></div>}

      <div style={{ ...styles.priceRow, marginTop: 10, padding: "10px 14px", background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8" }}>
        <span style={{ fontWeight: 700 }}>Total Before Loyalty Discount</span>
        <span style={{ fontWeight: 800 }}>₹{rawGrandTotal.toLocaleString()}</span>
      </div>

      {loyaltyPercent > 0 && <div style={styles.priceRow}><span>Loyalty Member Discount ({loyaltyPercent}%) <Tooltip title="This discount applies for loyalty membership."><InfoCircleOutlined style={{ marginLeft: 6, color: "#1890ff" }} /></Tooltip></span><span style={{ fontWeight: 700, color: "#cf1322" }}>-₹{loyaltyAmount.toLocaleString()}</span></div>}

      {gstPercent > 0 && (
        <>
          <div style={styles.priceRow}>
            <span style={{ fontWeight: 600 }}>GST Type</span>
            <Switch checked={gstType === "inclusive"} checkedChildren="Inclusive" unCheckedChildren="Exclusive" onChange={(checked) => onGstTypeChange?.(checked ? "inclusive" : "exclusive")} />
          </div>
          <div style={styles.priceRow}><span>GST ({gstPercent}%){gstType === "inclusive" ? " (Added to Total)" : ""}</span><span style={{ fontWeight: 700, color: gstType === "inclusive" ? "#cf1322" : "#237804" }}>₹{gstAmount.toLocaleString()}</span></div>
        </>
      )}

      <div style={{ ...styles.priceRow, marginTop: 10, padding: "10px 14px", background: "#f6ffed", borderRadius: 8, border: "1px solid #b7eb8f" }}>
        <span style={{ fontWeight: 700, color: "#237804" }}>Total Payable Amount</span>
        <span style={{ fontWeight: 800, fontSize: 18, color: "#237804" }}>₹{finalPayableTotal.toLocaleString()}</span>
      </div>

      <PaymentRowsBox
        paymentRows={paymentRows}
        finalPayableTotal={finalPayableTotal}
        finalRemaining={finalRemaining}
        totalAdvance={totalAdvance}
        totalDiscount={totalDiscount}
        paymentStatus={paymentStatus}
        addPaymentRow={addPaymentRow}
        updatePaymentRow={updatePaymentRow}
        removePaymentRow={removePaymentRow}
      />
    </div>
  );
};

const RoomBookingRow = ({ room, data, onChange, selectedId, onSelect }) => {
  const isSelected = selectedId === room.id;
  const statusCfg = STATUS_COLORS[room.status?.toLowerCase()] || STATUS_COLORS.available;
  const maxPersona = Number(room.max_occupancy || 0) + Number(room.additional_occupancy || 0);
  const adults = Number(data.guests || 1);
  const child = Number(data.child || 0);

  return (
    <div style={{ border: isSelected ? "1.5px solid #1677ff" : "1.5px solid #e8e8e8", borderRadius: 10, overflow: "hidden", background: "#fff", transition: "all .2s" }}>
      <div onClick={() => onSelect(room)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", background: isSelected ? "#e6f4ff" : "#fafafa", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HomeOutlined style={{ color: statusCfg.color, fontSize: 14 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>{room.room_name}</div>
            <div style={{ fontSize: 11, color: "#888" }}>#{room.room_number} · {room.room_type} · Floor {room.floor}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1677ff" }}>₹{toNum(room.price_per_day).toLocaleString()}/night</span>
          <StatusBadge status={room.status} />
          {isSelected ? <CloseOutlined style={{ color: "#1677ff", fontSize: 13 }} /> : <EditOutlined style={{ color: "#aaa", fontSize: 13 }} />}
        </div>
      </div>
      {isSelected && (
        <div style={{ padding: 16 }}>
          <div style={{ ...styles.formGrid, gridTemplateColumns: "1fr 1fr 1fr" }}>
            <FormField label="Adults">
              <NumberInput
                value={adults}
                min={1}
                max={maxPersona}
                onChange={(v) => {
                  if (v + child > maxPersona) {
                    notify.info(  `You cannot add more guests. The maximum occupancy for this room is ${maxPersona} guests.`);
                    return;
                  }
                  onChange(room.id, "guests", v);
                }}
              />
            </FormField>

            <FormField label="Child">
              <NumberInput
                value={child}
                min={0}
                max={maxPersona}
                disabled={!Number(room.child_cost_per_person)}
                onChange={(v) => {
                  if (!Number(room.child_cost_per_person)) {
                    notify.info("Children not added!");
                    return;
                  }
                  if (v + adults > maxPersona) {
                    notify.info(  `You cannot add more guests. The maximum occupancy for this room is ${maxPersona} guests.`);
                    return;
                  }
                  const childAge = Array.from({ length: v }, (_, i) => ({
                    name: `child-${i + 1} age`,
                    age: data.child_age?.[i]?.age ?? 0,
                  }));
                  onChange(room.id, "child", v);
                  onChange(room.id, "child_age", childAge);
                }}
              />
            </FormField>

            <FormField label="Payment Mode">
              <Select value={data.payment_mode || "Cash"} options={["Cash", "UPI", "Card", "Bank Transfer", "Advance"]} onChange={(v) => onChange(room.id, "payment_mode", v)} />
            </FormField>

            {Array.from({ length: Number(data.child || 0) }, (_, i) => (
              <FormField key={i} label={`Child-${i + 1} Age`}>
                <Select
                  value={data.child_age?.[i]?.age ?? 0}
                  onChange={(value) => {
                    const updatedAges = [...(data.child_age || [])];
                    updatedAges[i] = { name: `child-${i + 1} age`, age: Number(value) };
                    onChange(room.id, "child_age", updatedAges);
                  }}
                  options={[{ label: "0-1", value: 0 }, ...Array.from({ length: Number(room?.child_age_limit || 0) }, (_, ageIndex) => ({ label: `${ageIndex + 1}`, value: ageIndex + 1 }))]}
                />
              </FormField>
            ))}

            {toNum(room.ac_charge) > 0 && (
              <FormField label="AC Required" span={3}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 4 }}>
                  <input type="checkbox" checked={data.ac_required ?? true} onChange={(e) => onChange(room.id, "ac_required", e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>AC Required</span>
                  <span style={{ fontSize: 12, color: "#888" }}>(+₹{room.ac_charge}/stay)</span>
                </label>
              </FormField>
            )}

            <FormField label="Special Requests" span={3}>
              <Textarea placeholder="Specific requirements for this room…" value={data.special_requests || ""} onChange={(v) => onChange(room.id, "special_requests", v)} rows={2} />
            </FormField>
          </div>
        </div>
      )}
    </div>
  );
};

function SuccessScreen({
  rooms,
  form,
  nights,
  grandTotal,
  onBack,
  bookings = [],
  roomData = {},
  paymentRows = [],
  totalAdvance = 0,
  totalDiscount = 0,
  remainingPayable = 0,
  paymentStatus = "Pending",
  loyaltyDiscountPercent = 0,
  loyaltyDiscountAmount = 0,
  gst = 0,
  gstType = "exclusive",
  gstAmount = 0,
  headerImg = "",
  footerImg = "",
  hourlyBook = false,
  totalHours = 0,
  earlyCheckinAmount = 0,
}) {
  const bookIds = bookings.map((b) => b.booking_id || b.id).filter(Boolean);
  const totalPayable = Number(grandTotal || 0);

  const handlePrint = () => {
    generateBulkPDF({
      bookIds,
      rooms,
      form,
      nights,
      grandTotal: totalPayable,
      paidAmount: totalAdvance,
      receivedAdvance: totalAdvance,
      remainingPayable,
      discountAmount: totalDiscount,
      amountType: totalDiscount > 0 ? "discount" : totalAdvance > 0 ? "advance" : null,
      paymentStatus,
      roomData,
      paidAmounts: Object.fromEntries(rooms.map((r) => [r.id, 0])),
      paymentRows,
      loyaltyDiscountPercent,
      loyaltyDiscountAmount,
      gst,
      gstType,
      gstAmount,
      headerImg,
      footerImg,
      hourlyBook,
      totalHours,
      bookingType: hourlyBook ? "hourly" : "daily",
      early_checkin_amount: earlyCheckinAmount,
    });
  };

  const handlePrintA4 = () => {
    generateBulkA4PDF({
      bookIds,
      rooms,
      form,
      nights,
      grandTotal: totalPayable,
      paidAmount: totalAdvance,
      receivedAdvance: totalAdvance,
      remainingPayable,
      discountAmount: totalDiscount,
      amountType: totalDiscount > 0 ? "discount" : totalAdvance > 0 ? "advance" : null,
      paymentStatus,
      roomData,
      paidAmounts: Object.fromEntries(rooms.map((r) => [r.id, 0])),
      paymentRows,
      loyaltyDiscountPercent,
      loyaltyDiscountAmount,
      gst,
      gstType,
      gstAmount,
      headerImg,
      footerImg,
      hourlyBook,
      totalHours,
      bookingType: hourlyBook ? "hourly" : "daily",
      early_checkin_amount: earlyCheckinAmount,
    });
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "70vh" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 36, maxWidth: 620, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.10)", textAlign: "center" }}>
        <div style={{ fontSize: 56, color: "#52c41a", marginBottom: 8 }}><CheckCircleOutlined /></div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#111", marginBottom: 4 }}>Bulk Booking Confirmed!</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 24 }}>{rooms.length} rooms booked for <b>{form.guest_name || "Guest"}</b></div>

        <div style={{ textAlign: "left", background: "#fafafa", borderRadius: 10, padding: "16px 18px", marginBottom: 20 }}>
          {[
            ["Guest", form.guest_name],
            ["Phone", form.phone],
            ["Check-In", form.check_in],
            ["Check-Out", form.check_out],
            ["Booking Type", hourlyBook ? "Hourly" : "Daily"],
            [hourlyBook ? "Total Hours" : "Nights", hourlyBook ? totalHours : nights],
            ["Payment", form.payment_mode],
            ["Total Payable", `₹${totalPayable.toLocaleString()}`],
            ["Total Advance", `₹${Number(totalAdvance || 0).toLocaleString()}`],
            ["Total Discount", `₹${Number(totalDiscount || 0).toLocaleString()}`],
            ["Remaining Payable", `₹${Number(remainingPayable || 0).toLocaleString()}`],
            ["Payment Status", paymentStatus],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px dashed #f0f0f0", fontSize: 13 }}>
              <span style={{ color: "#888" }}>{k}</span><span style={{ fontWeight: 600 }}>{v || "—"}</span>
            </div>
          ))}

          {loyaltyDiscountPercent > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px dashed #f0f0f0", fontSize: 13 }}><span style={{ color: "#cf1322" }}>Loyalty Discount ({loyaltyDiscountPercent}%)</span><span style={{ fontWeight: 700, color: "#cf1322" }}>-₹{Number(loyaltyDiscountAmount || 0).toLocaleString()}</span></div>}
          {Number(gst || 0) > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px dashed #f0f0f0", fontSize: 13 }}><span style={{ color: "#888" }}>GST ({gst}%) - {gstType === "inclusive" ? "Inclusive" : "Exclusive"}</span><span style={{ fontWeight: 700 }}>₹{Number(gstAmount || 0).toLocaleString()}</span></div>}
          {earlyCheckinAmount > 0 && <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px dashed #f0f0f0", fontSize: 13 }}><span style={{ color: "#cf1322" }}>Early Check-in Charge</span><span style={{ fontWeight: 700, color: "#cf1322" }}>+₹{Number(earlyCheckinAmount).toLocaleString()}</span></div>}

          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>Rooms Booked</div>
            {rooms.map((r) => {
              const childAges = roomData[r.id]?.child_age || [];
              return (
                <div key={r.id} style={{ fontSize: 12, color: "#444", padding: "4px 0", borderBottom: "1px dashed #eee" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{r.room_name} #{r.room_number}</span>
                    <span style={{ color: "#1677ff", fontWeight: 600 }}>{roomData[r.id]?.guests || 1} Adult, {roomData[r.id]?.child || 0} Child</span>
                  </div>
                  {childAges.length > 0 && <div style={{ color: "#888", marginTop: 2 }}>Child Ages: {childAges.map((c) => (Number(c.age) === 0 ? "0-1Y" : `${c.age}Y`)).join(", ")}</div>}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Button size="large" icon={<PrinterOutlined />} onClick={() => { handlePrint(); handlePrintA4(); }} style={{ borderColor: "#6e3dd1", color: "#6e3dd1", fontWeight: 600 }} block>Print / Download Receipt</Button>
          <Button size="large" icon={<PrinterOutlined />} onClick={handlePrintA4} style={{ borderColor: "#237804", color: "#237804", fontWeight: 600 }} block>Print Bill in A4</Button>
          <Button type="primary" icon={<ArrowLeftOutlined />} size="large" onClick={onBack} block>Back to Rooms</Button>
        </div>
      </div>
    </div>
  );
}

const BulkRoomBooking = () => {
  const location = useLocation();
  const { selectedRooms = [] } = location.state || {};

  console.log("selectedRooms++++",selectedRooms);

  const map_room_id = selectedRooms.map((x)=>(
    x.id
  ))
  console.log("map_room_id",map_room_id);

  const todayDate = dayjs();
  const tomorrowDate = dayjs().add(1, "day");

  const [form, setForm] = useState({
    guest_name: "",
    phone: "",
    email: "",
    id_type: "Aadhaar",
    id_number: "",
    address: "",
    guests: selectedRooms.length || 1,
    check_in: todayDate.format("YYYY-MM-DD"),
    check_out: tomorrowDate.format("YYYY-MM-DD"),
    special_requests: "",
    payment_mode: "Cash",
    customer_gst: "",
    check_in_time: "",
    check_out_time: "",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const initRoomData = () => {
    const d = {};
    selectedRooms.forEach((r) => {
      d[r.id] = {
        guests: 1,
        child: 0,
        child_age: [],
        payment_mode: "Cash",
        special_requests: "",
        ac_required: true,
      };
    });
    return d;
  };

  const [roomData, setRoomData] = useState(initRoomData);
  const handleRoomField = (roomId, field, value) => setRoomData((p) => ({ ...p, [roomId]: { ...p[roomId], [field]: value } }));

  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docFiles, setDocFiles] = useState([]);
  const fileRef = useRef();

  const [exCustomer, setExCustomer] = useState([]);
  const [loyalDiscount, setLoyalDiscount] = useState(0);
  const [headerImg, setHeaderImg] = useState("");
  const [footerImg, setFooterImg] = useState("");
  const [gst, setGst] = useState("");
  const [gstType, setGstType] = useState("exclusive");
  const [hourlyBook, setHourlyBook] = useState(false);
  const [fullBooking, setFullBooking] = useState(false);
  const [defaultCheckInTime, setDefaultCheckInTime] = useState("");
  const [paymentRows, setPaymentRows] = useState([]);

  const nights = nightsBetween(form.check_in, form.check_out);

  const get_bulk_price = async (from, to, map_room_id) => {
          try {
              const req = await RoomServices.getBulkRoomByPrice(from, to, [map_room_id]);
              console.log("rooms spl prices via room", req.data);
              // setRoom(req.data[0])
              setRoomData(req.data[0])
  
          } catch (error) {
              console.log(error);
  
          }
      }
      
  useEffect(() => {

    get_bulk_price(todayDate.format("YYYY-MM-DD"), todayDate.format("YYYY-MM-DD"), map_room_id)
  }, [])

  const getTimeDiffHours = () => {
    if (!form.check_in || !form.check_out || !form.check_in_time || !form.check_out_time) return 1;
    const start = dayjs(`${form.check_in} ${form.check_in_time}`, "YYYY-MM-DD hh:mm A");
    const end = dayjs(`${form.check_out} ${form.check_out_time}`, "YYYY-MM-DD hh:mm A");
    const minutes = end.diff(start, "minute");
    return Math.max(Math.ceil(minutes / 60), 1);
  };

  const totalHours = getTimeDiffHours();

  const earlyCheckinAmount = (() => {
    if (fullBooking) return 0;
    if (!form.check_in_time || !defaultCheckInTime) return 0;
    const guestIn = dayjs(form.check_in_time, "hh:mm A");
    const defStr = typeof defaultCheckInTime === "object" ? defaultCheckInTime.format("hh:mm A") : String(defaultCheckInTime);
    const def = dayjs(defStr, "hh:mm A");
    if (!guestIn.isValid() || !def.isValid()) return 0;
    const guestMins = guestIn.hour() * 60 + guestIn.minute();
    const defMins = def.hour() * 60 + def.minute();
    if (guestMins < defMins) {
      const diffMins = defMins - guestMins;
      const avgPricePerHour = selectedRooms.length > 0 ? selectedRooms.reduce((sum, r) => sum + toNum(r.price_per_hour), 0) / selectedRooms.length : 0;
      return Math.round((diffMins / 60) * avgPricePerHour) * selectedRooms.length;
    }
    return 0;
  })();

  const rawRoomsTotal = selectedRooms.reduce((sum, r) => sum + getRoomCalc({ room: r, data: roomData[r.id], nights, hourlyBook, totalHours, fullBooking }).total, 0);
  const rawGrandTotal = rawRoomsTotal + earlyCheckinAmount;
  const loyaltyDiscountPercent = Number(loyalDiscount || 0);
  const loyaltyDiscountAmount = loyaltyDiscountPercent > 0 ? Number(((rawGrandTotal * loyaltyDiscountPercent) / 100).toFixed(2)) : 0;
  const payableTotal = Math.max(rawGrandTotal - loyaltyDiscountAmount, 0);
  const gstPercent = Number(gst || 0);
  const gstAmount = gstPercent > 0 ? Math.round((payableTotal * gstPercent) / 100) : 0;
  const finalPayableTotal = gstType === "inclusive" ? Number((payableTotal + gstAmount).toFixed(2)) : payableTotal;

  const totalAdvance = paymentRows.filter((row) => row.type === "advance").reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const totalDiscount = paymentRows.filter((row) => row.type === "discount").reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const finalPayableAfterDiscount = Math.max(finalPayableTotal - totalDiscount, 0);
  const finalRemaining = Math.max(finalPayableAfterDiscount - totalAdvance, 0);
  const amountType = totalDiscount > 0 ? "discount" : totalAdvance > 0 ? "advance" : null;
  const paymentStatus = totalDiscount > 0 && finalRemaining === 0 ? "Discount Applied / Fully Paid" : totalDiscount > 0 && totalAdvance > 0 ? "Advance & Discount Applied" : finalRemaining > 0 && totalAdvance > 0 ? "Advance Received" : finalRemaining === 0 && finalPayableTotal > 0 ? "Fully Paid" : "Pending";

  const getUsedAmountExceptCurrent = (rows, currentKey) => rows.filter((row) => row.key !== currentKey).reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const addPaymentRow = (type = "advance") => {
    if (finalRemaining <= 0) {
      notify.info("Remaining payable is already 0. Cannot add advance or discount.");
      return;
    }
    setPaymentRows((prev) => [...prev, { key: `${Date.now()}-${Math.random()}`, type, amount: 0, mode: form.payment_mode || "Cash", date: dayjs().format("YYYY-MM-DD") }]);
  };

  const updatePaymentRow = (key, field, value) => {
    setPaymentRows((prev) => {
      if (field === "amount") {
        const enteredAmount = Math.max(0, Number(value || 0));
        const usedAmountExceptCurrent = getUsedAmountExceptCurrent(prev, key);
        const allowedAmount = Math.max(finalPayableTotal - usedAmountExceptCurrent, 0);
        if (enteredAmount > allowedAmount) notify.info(`Amount cannot exceed remaining payable ₹${allowedAmount.toLocaleString()}`);
        return prev.map((row) => row.key === key ? { ...row, amount: Math.min(enteredAmount, allowedAmount) } : row);
      }
      return prev.map((row) => row.key === key ? { ...row, [field]: value } : row);
    });
  };

  const removePaymentRow = (key) => setPaymentRows((prev) => prev.filter((row) => row.key !== key));

  useEffect(() => {
    setPaymentRows([]);
  }, [nights, totalHours, hourlyBook, fullBooking, gstType, loyaltyDiscountAmount]);

  useEffect(() => {
    getExistingCust();
    getCheckTime();
  }, []);

  const getCheckTime = async () => {
    try {
      const req = await RoomServices.getAllByTable("utills");
      const utils = req?.data?.[0] || {};
      setHeaderImg(utils?.header_img || "");
      setFooterImg(utils?.footer_img || "");
      setGst(utils?.gst || "");
      setDefaultCheckInTime(utils?.checkin_time ? dayjs(utils?.checkin_time, "hh:mm A") : "");
      setForm((prev) => ({ ...prev, check_in_time: prev.check_in_time || utils?.checkin_time || "", check_out_time: prev.check_out_time || utils?.checkout_time || "" }));
    } catch (error) {
      console.error(error);
    }
  };

  const getExistingCust = async () => {
    try {
      const req = await GeneralService.get("book_room");
      const uniqueCustomers = Object.values((req?.data || []).filter((item) => item.phone && item.phone.toString().trim() !== "").reduce((acc, item) => { acc[item.phone] = item; return acc; }, {}));
      setExCustomer(uniqueCustomers);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePhoneChange = async (phoneValue) => {
    set("phone", phoneValue);
    setErrors((p) => ({ ...p, phone: "" }));
    const selectedCustomer = exCustomer.find((c) => String(c.phone || "").trim() === String(phoneValue || "").trim());
    if (!selectedCustomer) {
      setLoyalDiscount(0);
      return;
    }
    try {
      const req = await GeneralService.loyality_discount(selectedCustomer.phone);
      if (req?.eligible) {
        notify.success("Congratulations!", req.message);
        setLoyalDiscount(Number(req.discount || 0));
      } else {
        notify.info(req?.message || "Loyalty offer not available.");
        setLoyalDiscount(0);
      }
    } catch (error) {
      console.error(error);
      setLoyalDiscount(0);
    }
    setForm((prev) => ({ ...prev, phone: selectedCustomer.phone || phoneValue, guest_name: selectedCustomer.guest_name || "", customer_gst: selectedCustomer.customer_gst || "", email: selectedCustomer.email || "", id_type: selectedCustomer.id_type || "Aadhaar", id_number: selectedCustomer.id_number || "", address: selectedCustomer.address || "" }));
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setDocFiles((p) => [...p, ...files]);
    setDocs((p) => [...p, ...files.map((f) => ({ name: f.name, size: (f.size / 1024).toFixed(1) + " KB", type: f.type, url: URL.createObjectURL(f) }))]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeDoc = (i) => {
    setDocs((p) => p.filter((_, idx) => idx !== i));
    setDocFiles((p) => p.filter((_, idx) => idx !== i));
  };

  const validate = (type) => {
    const e = {};
    if (type !== "reserved") {
      if (!form.guest_name.trim()) e.guest_name = "Required";
      if (!form.phone.trim()) e.phone = "Required";
      if (!form.id_number.trim()) e.id_number = "Required";
    }
    if (!form.check_in || !form.check_out) e.date_range = "Required";
    if (nights <= 0) e.date_range = "Check-out must be after check-in";
    return e;
  };

  const getRoomPaymentShare = (room, roomRawTotal, index) => {
    const allRoomTotals = selectedRooms.map((r) => getRoomCalc({ room: r, data: roomData[r.id], nights, hourlyBook, totalHours, fullBooking }).total);
    const roomsTotal = allRoomTotals.reduce((a, b) => a + b, 0);
    const roomLoyaltyAmount = rawGrandTotal > 0 ? Math.round((roomRawTotal / rawGrandTotal) * loyaltyDiscountAmount) : 0;
    const roomPayableBeforeGst = Math.max(roomRawTotal - roomLoyaltyAmount, 0);
    const roomGstAmount = gstPercent > 0 && payableTotal > 0 ? Math.round((roomPayableBeforeGst / payableTotal) * gstAmount) : 0;
    const roomPayableTotal = gstType === "inclusive" ? roomPayableBeforeGst + roomGstAmount : roomPayableBeforeGst;
    const ratio = finalPayableTotal > 0 ? roomPayableTotal / finalPayableTotal : 0;
    const paidAmount = index === selectedRooms.length - 1
      ? Math.max(0, totalAdvance - selectedRooms.slice(0, -1).reduce((sum, r, i) => {
          const c = getRoomCalc({ room: r, data: roomData[r.id], nights, hourlyBook, totalHours, fullBooking });
          const shareRatio = finalPayableTotal > 0 ? c.total / rawRoomsTotal : 0;
          return sum + Math.round(totalAdvance * shareRatio);
        }, 0))
      : Math.round(totalAdvance * ratio);
    const discountAmount = Math.round(totalDiscount * ratio);
    const remaining = Math.max(roomPayableTotal - discountAmount - paidAmount, 0);
    return { roomLoyaltyAmount, roomGstAmount, roomPayableTotal, paidAmount, discountAmount, remaining };
  };

  const handleSubmit = async (type) => {
    const e = validate(type);
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    const payload = {
      ...form,
      status: type,
      full_book: fullBooking,
      rooms: selectedRooms.map((r, index) => {
        const data = roomData[r.id] || {};
        const calc = getRoomCalc({ room: r, data, nights, hourlyBook, totalHours, fullBooking });
        const share = getRoomPaymentShare(r, calc.total, index);
        const pricing_summary = {
          booking_type: hourlyBook ? "hourly" : "daily",
          rate_per_night: toNum(r.price_per_day),
          duration_nights: hourlyBook ? 0 : nights,
          rate_per_hour: toNum(r.price_per_hour),
          duration_hours: hourlyBook ? totalHours : 0,
          base_total: calc.baseTotal,
          extra_guests: calc.extraGuests,
          extra_cost_per_night: calc.extraCostPerNight,
          extra_total: calc.extraTotal,
          ac_charge: data.ac_required !== false ? toNum(r.ac_charge) : 0,
          ac_charge_total: calc.acChargeTotal,
          full_book: calc.fullBookTotal,
          full_book_charge: fullBooking ? toNum(r.additional_24_hour) : 0,
          total_before_loyalty_discount: calc.total,
          loyalty_discount_percent: loyaltyDiscountPercent,
          loyalty_discount_amount: share.roomLoyaltyAmount,
          total_amount: share.roomPayableTotal,
          paid_amount: share.paidAmount,
          received_advance: share.paidAmount,
          remaining_payable: share.remaining,
          discount_amount: share.discountAmount,
          amount_type: amountType,
          payment_status: share.remaining === 0 ? "Fully Paid" : totalDiscount > 0 && totalAdvance > 0 ? "Advance & Discount Applied" : totalDiscount > 0 ? "Discount Applied" : totalAdvance > 0 ? "Advance Received" : "Pending",
          payment_rows: paymentRows,
          customer_gst: form?.customer_gst || "",
          gst_type: gstType,
          gst_percent: gstPercent,
          gst_amount: share.roomGstAmount,
          early_checkin_amount: index === 0 ? earlyCheckinAmount : 0,
          extra_child: calc.extraChild,
          extra_child_cost_per_night: calc.extraChildCostPerNight,
          extra_child_total: calc.extraChildTotal,
          child_cost_per_person: calc.childCostPerPerson,
        };
        return {
          room_id: r.id,
          room_data: r,
          guests: data.guests || 1,
          child: Number(data.child || 0),
          child_age: data.child_age || [],
          payment_mode: data.payment_mode || form.payment_mode,
          special_requests: data.special_requests || form.special_requests,
          ac_required: data.ac_required !== false,
          paid_amount: share.paidAmount,
          payment_rows: paymentRows,
          pricing_summary,
        };
      }),
      ac_required: Object.fromEntries(selectedRooms.map((r) => [r.id, roomData[r.id]?.ac_required !== false])),
      amount_type: amountType,
      paid_amount: totalAdvance,
      payment_rows: paymentRows,
      pricing_summary: {
        booking_type: hourlyBook ? "hourly" : "daily",
        duration_nights: hourlyBook ? 0 : nights,
        duration_hours: hourlyBook ? totalHours : 0,
        total_before_loyalty_discount: rawGrandTotal,
        loyalty_discount_percent: loyaltyDiscountPercent,
        loyalty_discount_amount: loyaltyDiscountAmount,
        total_amount: finalPayableTotal,
        paid_amount: totalAdvance,
        received_advance: totalAdvance,
        remaining_payable: finalRemaining,
        discount_amount: totalDiscount,
        amount_type: amountType,
        payment_status: paymentStatus,
        payment_rows: paymentRows,
        customer_gst: form?.customer_gst || "",
        gst_type: gstType,
        gst_percent: gstPercent,
        gst_amount: gstAmount,
        early_checkin_amount: earlyCheckinAmount,
        full_book: fullBooking,
      },
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([k, v]) => formData.append(k, typeof v === "object" ? JSON.stringify(v) : v));
    docFiles.forEach((f) => formData.append("documents", f));

    try {
      setLoading(true);
      const res = await fetch(ENDPOINTS.BULK_BOOKROOM || "/api/bulk-book-room", { method: "POST", body: formData });
      const data = await res.json();
      if (data.status) {
        setBookings(data.bookings || []);
        setSubmitted(true);
      } else {
        notify.error("Booking failed: " + data.message);
      }
    } catch (err) {
      console.error(err);
      notify.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const dropdownItems = [
    { key: "occupied", label: <span style={{ color: "green" }}><CheckCircleOutlined style={{ marginRight: 7, fontSize: 16 }} /> Confirm All Bookings</span> },
    { key: "reserved", label: <span style={{ color: "#d46b08" }}><CalendarOutlined style={{ marginRight: 7, fontSize: 16 }} /> Reserve All Rooms</span> },
  ];

  if (!canViewModule(MODULE_KEY)) {
    return <Card><Empty description="No permission to view this page" /></Card>;
  }

  if (submitted) {
    return (
      <SuccessScreen
        bookings={bookings}
        rooms={selectedRooms}
        form={form}
        nights={nights}
        grandTotal={finalPayableTotal}
        roomData={roomData}
        paymentRows={paymentRows}
        totalAdvance={totalAdvance}
        totalDiscount={totalDiscount}
        remainingPayable={finalRemaining}
        paymentStatus={paymentStatus}
        loyaltyDiscountPercent={loyaltyDiscountPercent}
        loyaltyDiscountAmount={loyaltyDiscountAmount}
        gst={gstPercent}
        gstType={gstType}
        gstAmount={gstAmount}
        headerImg={headerImg ? `${ENDPOINTS.IMG_HEADER}${headerImg}` : ""}
        footerImg={footerImg ? `${ENDPOINTS.IMG_HEADER}${footerImg}` : ""}
        hourlyBook={hourlyBook}
        earlyCheckinAmount={earlyCheckinAmount}
        totalHours={totalHours}
        onBack={() => navigate(-1)}
      />
    );
  }

  if (!selectedRooms.length) {
    return <Card><Empty description="No rooms selected for bulk booking." /><div style={{ textAlign: "center", marginTop: 16 }}><Button onClick={() => navigate(-1)}>Go Back</Button></div></Card>;
  }

  return (
    <Card
      title={
        <div style={{ ...css.cardHeader, padding: "15px 0 15px 0" }}>
          <div style={css.backBtn} onClick={() => navigate(-1)} onMouseEnter={(e) => { e.currentTarget.style.background = T.accentSoft; e.currentTarget.style.transform = "scale(1.06)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = T.accentLight; e.currentTarget.style.transform = "scale(1)"; }}><ArrowLeftOutlined /></div>
          <div><div style={css.pageTitle}>Bulk Room Booking</div><div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>Manage multiple room bookings &amp; group reservations efficiently</div></div>
        </div>
      }
      extra={<Space><Tag color="blue" style={{ fontSize: 13, padding: "4px 12px", borderRadius: 20 }}><AppstoreOutlined style={{ marginRight: 5 }} />{selectedRooms.length} Rooms Selected</Tag></Space>}
    >
      <div style={styles.layout}>
        <div style={styles.leftPanel}>
          <SectionCard title="Selected Rooms" icon={<HomeOutlined />}>
            <RoomSummaryCard rooms={selectedRooms} selectedId={selectedRoomId} onSelect={(r) => setSelectedRoomId((prev) => prev === r.id ? null : r.id)} />
          </SectionCard>
          <SectionCard title="Pricing Summary" icon={<BankOutlined />}>
            <PricingTable
              rooms={selectedRooms}
              nights={nights}
              roomData={roomData}
              loyaltyDiscountPercent={loyaltyDiscountPercent}
              gst={gstPercent}
              gstType={gstType}
              onGstTypeChange={setGstType}
              hourlyBook={hourlyBook}
              totalHours={totalHours}
              fullBooking={fullBooking}
              earlyCheckinAmount={earlyCheckinAmount}
              paymentRows={paymentRows}
              finalPayableTotal={finalPayableTotal}
              finalRemaining={finalRemaining}
              totalAdvance={totalAdvance}
              totalDiscount={totalDiscount}
              paymentStatus={paymentStatus}
              addPaymentRow={addPaymentRow}
              updatePaymentRow={updatePaymentRow}
              removePaymentRow={removePaymentRow}
            />
          </SectionCard>
        </div>

        <div style={styles.rightPanel}>
          <SectionCard title="Guest Information" icon={<UserOutlined />}>
            <div style={{ background: "#e6f4ff", border: "1px solid #91caff", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#0050b3", display: "flex", alignItems: "center", gap: 8 }}>
              <TeamOutlined style={{ fontSize: 16 }} /> These details apply to <strong>all rooms</strong>. You can customise per-room settings below.
            </div>
            <div style={{ ...styles.formGrid, gridTemplateColumns: "1fr 1fr" }}>
              <FormField label="Phone Number" required error={errors.phone}>
                <div style={styles.inputWrap}>
                  <span style={styles.inputPrefix}><PhoneOutlined /></span>
                  <input list="bulk-customer-phone-list" placeholder="+91 XXXXX XXXXX" value={form.phone || ""} onChange={(e) => handlePhoneChange(e.target.value)} style={{ ...styles.input, paddingLeft: 34 }} />
                  <datalist id="bulk-customer-phone-list">
                    {exCustomer.map((cust) => <option key={cust.id || cust.phone} value={cust.phone}>{cust.guest_name ? `${cust.guest_name} - ${cust.phone}` : cust.phone}</option>)}
                  </datalist>
                </div>
              </FormField>
              <FormField label="Full Name" required error={errors.guest_name}><Input prefix={<UserOutlined />} placeholder="e.g. Ravi Kumar" value={form.guest_name} onChange={(v) => { set("guest_name", v); setErrors((p) => ({ ...p, guest_name: "" })); }} /></FormField>
              <FormField label="Customer GST" error={errors.customer_gst}><Input prefix={<InfoOutlined />} placeholder="e.g. GSTIN***" value={form.customer_gst} onChange={(v) => { set("customer_gst", v); setErrors((p) => ({ ...p, customer_gst: "" })); }} /></FormField>
              <FormField label="Email Address"><Input prefix={<MailOutlined />} placeholder="email@example.com" value={form.email} onChange={(v) => set("email", v)} /></FormField>
              <FormField label="ID Type"><Select value={form.id_type} options={["Aadhaar", "Passport", "Driving License", "Voter ID", "PAN Card"]} onChange={(v) => set("id_type", v)} /></FormField>
              <FormField label="ID Number" required error={errors.id_number}><Input prefix={<IdcardOutlined />} placeholder="Enter ID number" value={form.id_number} onChange={(v) => { set("id_number", v); setErrors((p) => ({ ...p, id_number: "" })); }} /></FormField>
              <FormField label="Address" span={2}><Textarea placeholder="Full address of the guest" value={form.address} onChange={(v) => set("address", v)} rows={2} /></FormField>
            </div>
          </SectionCard>

          <SectionCard title="Booking Details" icon={<CalendarOutlined />} extra={
            <>
              <FormField label=" "><label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 8 }}><input type="checkbox" checked={hourlyBook} onChange={(e) => { setHourlyBook(e.target.checked); if (e.target.checked) setFullBooking(false); }} style={{ width: 16, height: 16, cursor: "pointer" }} /><span style={{ fontSize: 13, fontWeight: 500 }}>Hourly Room Book {hourlyBook ? `(${totalHours} ${totalHours === 1 ? "hour" : "hours"})` : ""}</span></label></FormField>
              <FormField label=" "><label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginTop: 8 }}><input type="checkbox" checked={fullBooking} onChange={(e) => { setFullBooking(e.target.checked); if (e.target.checked) setHourlyBook(false); }} style={{ width: 16, height: 16, cursor: "pointer" }} /><span style={{ fontSize: 13, fontWeight: 500 }}>Book For 24 Hours</span></label></FormField>
            </>
          }>
            <div style={{ ...styles.formGrid, gridTemplateColumns: "1fr 1fr 1fr" }}>
              <FormField label={`Stay Duration ${nights ? `(${nights} ${nights === 1 ? "night" : "nights"})` : ""}`} required error={errors.date_range}>
                <RangePicker
                  style={{ width: "100%", height: 38 }}
                  value={form.check_in && form.check_out ? [dayjs(form.check_in, "YYYY-MM-DD"), dayjs(form.check_out, "YYYY-MM-DD")] : null}
                  onChange={(dates) => {
                    if (!dates) {
                      setForm((p) => ({ ...p, check_in: "", check_out: "" }));
                      return;
                    }
                    setForm((p) => ({ ...p, check_in: dates[0].format("YYYY-MM-DD"), check_out: dates[1].format("YYYY-MM-DD") }));
                    setErrors((p) => ({ ...p, date_range: "" }));
                  }}
                  disabledDate={(current) => current && current.isBefore(dayjs().startOf("day"))}
                  format="DD-MM-YYYY"
                  allowClear
                />
              </FormField>
              <FormField label="Check-in Time"><TimePicker style={{ width: "100%", height: 38 }} format="hh:mm A" use12Hours value={form.check_in_time ? dayjs(form.check_in_time, "hh:mm A") : null} onChange={(time, timeString) => set("check_in_time", timeString)} allowClear /></FormField>
              <FormField label="Check-out Time"><TimePicker style={{ width: "100%", height: 38 }} format="hh:mm A" use12Hours value={form.check_out_time ? dayjs(form.check_out_time, "hh:mm A") : null} onChange={(time, timeString) => set("check_out_time", timeString)} allowClear /></FormField>
              <FormField label="Total Rooms"><NumberInput value={form.guests} min={1} onChange={(v) => set("guests", v)} /></FormField>
              <FormField label="Default Payment"><Select value={form.payment_mode} options={["Cash", "UPI", "Card", "Bank Transfer", "Advance"]} onChange={(v) => set("payment_mode", v)} /></FormField>
              <FormField label="Shared Requests / Notes" span={3}><Textarea placeholder="Group notes, special requirements applicable to all rooms…" value={form.special_requests} onChange={(v) => set("special_requests", v)} rows={3} /></FormField>
            </div>
          </SectionCard>

          <SectionCard title="Per-Room Settings" icon={<AppstoreOutlined />}>
            <p style={{ fontSize: 12.5, color: "#666", margin: "0 0 14px", lineHeight: 1.6 }}>Click on a room to customise adults, child, child age, payment mode, or special requests individually.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {selectedRooms.map((room) => <RoomBookingRow key={room.id} room={room} data={roomData[room.id] || {}} onChange={handleRoomField} selectedId={selectedRoomId} onSelect={(r) => setSelectedRoomId((prev) => prev === r.id ? null : r.id)} />)}
            </div>
          </SectionCard>

          <SectionCard title="Document Upload" icon={<FileTextOutlined />}>
            <p style={{ fontSize: 12.5, color: "#666", margin: "0 0 14px", lineHeight: 1.6 }}>Upload the guest's ID proof. This applies to the group booking as a whole.</p>
            <div style={styles.dropZone} onClick={() => fileRef.current.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleFiles({ target: { files: e.dataTransfer.files } }); }}>
              <CloudUploadOutlined style={{ fontSize: 32, color: "#1677ff", marginBottom: 8 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>Click or drag files here</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>JPG, PNG, PDF · Max 10MB each</div>
              <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" style={{ display: "none" }} onChange={handleFiles} />
            </div>
            {docs.length > 0 && <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>{docs.map((d, i) => <div key={i} style={styles.docItem}><FileTextOutlined style={{ fontSize: 18, color: "#1677ff", flexShrink: 0 }} /><div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 12.5, fontWeight: 600, color: "#222", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div><div style={{ fontSize: 11, color: "#aaa" }}>{d.size}</div></div><a href={d.url} target="_blank" rel="noreferrer" style={styles.docBtn}><EyeOutlined /></a><button type="button" style={{ ...styles.docBtn, color: "#ff4d4f" }} onClick={() => removeDoc(i)}><DeleteOutlined /></button></div>)}</div>}
          </SectionCard>

          <div style={styles.cta}>
            <Button danger size="large" onClick={() => navigate(-1)}>Cancel</Button>
            <Tooltip title={!canCreateModule(MODULE_KEY) ? "No permission to book rooms" : ""}>
              <Dropdown menu={{ items: dropdownItems, onClick: ({ key }) => handleSubmit(key) }} size="large" disabled={!canCreateModule(MODULE_KEY)}>
                <Button icon={<DownOutlined />} size="large" type="primary" loading={loading} disabled={!canCreateModule(MODULE_KEY)}>Book {selectedRooms.length} Rooms</Button>
              </Dropdown>
            </Tooltip>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default BulkRoomBooking;
